import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { VitalType } from '../../lib/api';
import { useHealthData } from '../../hooks/api';
import { useMonthNavigation, usePatient } from '../../hooks';
import {
  TemperatureChart,
  BloodPressureChart,
  SpO2Chart,
  MealHydrationTable,
  MonthlyAverage,
} from './components';
import { ErrorMessage, EmptyState, PageDataBoundary, PatientSwitcher } from '../../components';
import { formatDateForChartAxis, formatMealIntake } from '../../utils';
import { ContentEvents } from '../../lib/analytics';
import {
  TemperatureIcon,
  BloodPressureIcon,
  SpO2Icon,
  MealIcon,
  HydrationIcon,
  ArrowLeftIcon,
  ArrowRightIcon
} from '../../components/Icons';

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  max-width: 100vw;
  overflow-x: hidden;
  padding-bottom: 70px;
`;

const DateRangeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.md};
  gap: ${({ theme }) => theme.spacing.md};
`;

const DateNavButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};

  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const DateRangeText = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 24px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  min-width: 200px;
  text-align: center;
`;

const TabContainer = styled.div`
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 8px;
  overflow: hidden;
  margin: 0 ${({ theme }) => theme.spacing.md};
  height: 44px;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.sm};
  background-color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.background};
  color: ${({ $active, theme }) => $active ? 'white' : theme.colors.primary};
  border: none;
  border-right: 1px solid ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:last-child {
    border-right: none;
  }
`;

const Content = styled.div`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.md};
`;

const GraphCard = styled.div`
  background: ${({ theme }) => theme.colors.background};
`;

const ChartWrapper = styled.div`
  width: 100%;
  height: 350px;
`;

/**
 * ヘルスグラフページ - リファクタリング版
 * 体温・血圧脈拍・食事水分のグラフを表示
 */
export const HealthGraphPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialVital = (searchParams.get('vital') as VitalType) || 'temperature';

  // PatientContext から患者選択状態を取得
  // ※ linkedPatients の取得完了は AppReadyGate で保証済みのため isLoading は受け取らない
  const {
    selectedPatientId: patientId,
    selectedPatientIndex,
    linkedPatients,
    selectPatientByIndex,
  } = usePatient();

  // 選択中のバイタル種別
  const [selectedVital, setSelectedVital] = useState<VitalType>(initialVital);

  // 患者切り替えハンドラー
  const handlePatientChange = (index: number) => {
    selectPatientByIndex(index);
  };

  // 月ナビゲーションフックを使用（患者IDを渡して切り替え検知）
  const { currentMonth, goToPrevMonth, goToNextMonth } = useMonthNavigation(undefined, true, patientId);

  const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
  const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');

  const { data: healthDataResponse, isLoading, error } = useHealthData(
    patientId,
    startDate,
    endDate
  );

  // APIレスポンスからデータを抽出
  const healthData = useMemo(() => {
    if (!healthDataResponse?.health_data) return [];

    // 食事が提供なしの場合、食事記録を削除しておく
    return healthDataResponse.health_data.map((point) => ({
      ...point,
      meals: point.meals?.is_not_provided ? undefined : point.meals,
    }));
  }, [healthDataResponse]);
  const summary = healthDataResponse?.summary;

  // Analytics: 健康グラフ閲覧イベント（データ取得成功時）
  useEffect(() => {
    if (healthDataResponse && patientId) {
      const period = format(currentMonth, 'yyyy-MM');
      ContentEvents.viewHealthGraph(period);
    }
  }, [healthDataResponse, patientId, currentMonth]);


  // 日付範囲テキストの取得
  const getDateRangeText = () => {
    return format(currentMonth, 'yyyy年M月');
  };

  // 月の移動ハンドラー
  const handlePreviousMonth = () => {
    goToPrevMonth();
  };

  const handleNextMonth = () => {
    // 未来の月には進めない
    const nextMonthStart = new Date(currentMonth);
    nextMonthStart.setMonth(nextMonthStart.getMonth() + 1);
    if (nextMonthStart <= new Date()) {
      goToNextMonth();
    }
  };

  // 月平均値の計算（APIから返却されたsummaryデータを使用）
  const monthlyAverage = useMemo(() => {
    if (!summary) return null;

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;

    if (selectedVital === 'temperature') {
      if (!summary.temperature) return null;
      return {
        label: '体温',
        value: summary.temperature.avg.toFixed(1),
        unit: '°C',
        icon: <TemperatureIcon />,
        year,
        month,
      };
    } else if (selectedVital === 'blood_pressure_pulse') {
      if (!summary.blood_pressure_sys || !summary.blood_pressure_dia || !summary.pulse) return null;
      return {
        label: '血圧脈拍',
        value: `${Math.round(summary.blood_pressure_sys.avg)}/${Math.round(summary.blood_pressure_dia.avg)}`,
        unit: 'mmHg',
        pulseValue: Math.round(summary.pulse.avg).toString(),
        pulseUnit: 'bpm',
        icon: <BloodPressureIcon />,
        year,
        month,
      };
    } else if (selectedVital === 'spo2') {
      if (!summary.spo2) return null;
      return {
        label: 'SpO2',
        value: Math.round(summary.spo2.avg).toString(),
        unit: '%',
        icon: <SpO2Icon />,
        year,
        month,
      };
    } else if (selectedVital === 'hydration') {
      if (summary.meals?.staple_intake?.avg == null
        && summary.meals?.side_intake?.avg == null
        && summary.meals?.soup_intake?.avg == null
        && summary.hydration?.avg == null) {
          return null;
      }

      // summaryから食事の平均値を取得
      const stapleAvg = summary.meals?.staple_intake?.avg ?? 0;
      const sideAvg = summary.meals?.side_intake?.avg ?? 0;
      const soupAvg = summary.meals?.soup_intake?.avg ?? 0;

      // 平均値を「〜割」形式にフォーマット
      const stapleDisplay = formatMealIntake(Math.round(stapleAvg));
      const sideDisplay = formatMealIntake(Math.round(sideAvg));
      const soupDisplay = formatMealIntake(Math.round(soupAvg));

      const hydrationAvg = summary.hydration?.avg;
      const hydrationDisplay = hydrationAvg != null ? Math.round(hydrationAvg).toString() : undefined;

      return {
        label: '食事水分',
        value: '',
        unit: '',
        mainMealLabel: '主食',
        mainMealValue: stapleDisplay,
        sideMealLabel: '副食',
        sideMealValue: sideDisplay,
        soupMealLabel: '汁食',
        soupMealValue: soupDisplay,
        hydrationLabel: '水分',
        hydrationValue: hydrationDisplay,
        hydrationUnit: 'ml',
        icon: <MealIcon />,
        waterIcon: <HydrationIcon />,
        year,
        month,
      };
    }
    return null;
  }, [selectedVital, summary, currentMonth]);

  // グラフ用データの準備（APIデータを変換）
  const chartData = useMemo(() => {
    return healthData
      .filter((point) => {
        // 選択されたバイタルに応じてデータがある日のみフィルタリング
        if (selectedVital === 'temperature') {
          return point.vitals?.temp_c != null;
        } else if (selectedVital === 'blood_pressure_pulse') {
          return point.vitals?.sbp != null || point.vitals?.pulse != null;
        } else if (selectedVital === 'spo2') {
          return point.vitals?.spo2 != null;
        } else if (selectedVital === 'hydration') {
          // 食事（主食・副食・汁食）または水分データがある日のみ表示
          return point.meals != null || point.hydration_ml != null;
        }
        return true;
      })
      .map((point) => {
        // 選択されたバイタルに応じて返却するプロパティを変更
        if (selectedVital === 'temperature') {
          return {
            date: formatDateForChartAxis(point.date),
            originalDate: point.date,
            temperature: point.vitals?.temp_c || null,
          }
        } else if (selectedVital === 'blood_pressure_pulse') {
          return {
            date: formatDateForChartAxis(point.date),
            originalDate: point.date,
            blood_pressure_sys: point.vitals?.sbp || null,
            blood_pressure_dia: point.vitals?.dbp || null,
            pulse: point.vitals?.pulse || null,
          }
        } else if (selectedVital === 'spo2') {
          return {
            date: formatDateForChartAxis(point.date),
            originalDate: point.date,
            spo2: point.vitals?.spo2 || null,
          }
        }
        return null;
      });
  }, [selectedVital, healthData]);

  // SpO2データが存在するかを判定
  const hasSpo2Data = useMemo(() => {
    return healthData.some((point) => point.vitals?.spo2 != null && point.vitals.spo2 > 0);
  }, [healthData]);

  // 食事データが存在するかを判定
  const hasMealData = useMemo(() => {
    return healthData.some((point) => {
      return (
        point.meals?.staple_intake != null ||
        point.meals?.side_intake != null ||
        point.meals?.soup_intake != null
      );
    });
  }, [healthData]);

  // 水分データが存在するかを判定
  const hasHydrationData = useMemo(() => {
    return healthData.some((point) => point.hydration_ml != null);
  }, [healthData]);

  // 患者データがない場合
  if (!linkedPatients || linkedPatients.length === 0) {
    return (
      <EmptyState
        fullPage
        title="連携しているご家族が見つかりません"
        description="事業所と連携を行ってください"
        icon="👤"
      />
    );
  }

  // グラフのレンダリング
  const renderChart = () => {
    if (healthData.length === 0) {
      return (
        <EmptyState
          title="データがありません"
          description="選択された期間に記録がありません"
          icon="📊"
        />
      );
    }

    if (selectedVital === 'temperature') {
      return (
        <ChartWrapper>
          <TemperatureChart data={chartData} />
        </ChartWrapper>
      );
    } else if (selectedVital === 'blood_pressure_pulse') {
      return (
        <ChartWrapper>
          <BloodPressureChart data={chartData} />
        </ChartWrapper>
      );
    } else if (selectedVital === 'spo2') {
      return (
        <ChartWrapper>
          <SpO2Chart data={chartData} />
        </ChartWrapper>
      );
    } else if (selectedVital === 'hydration') {
      // 食事または水分データがある日のみをフィルタリングして表示
      const filteredHealthData = healthData.filter(
        point => point.meals != null || point.hydration_ml != null
      );

      // データがない場合はEmptyStateを表示（平均値も非表示）
      if (filteredHealthData.length === 0) {
        return (
          <EmptyState
            title="データがありません"
            description="選択された期間に記録がありません"
            icon="📊"
          />
        );
      }

      return <MealHydrationTable healthData={filteredHealthData} />;
    }

    return null;
  };

  return (
    <PageDataBoundary ready={!isLoading}>
      <Container>
        {/* 利用者切り替えタブ */}
        <PatientSwitcher
          patients={linkedPatients}
          selectedIndex={selectedPatientIndex}
          onPatientChange={handlePatientChange}
        />

        {error ? (
          <Content>
            <ErrorMessage
              title="データの取得に失敗しました"
              description="ネットワーク接続を確認してください"
            />
          </Content>
        ) : (
          <>
            {/* 日付範囲表示 */}
            <DateRangeContainer>
              <DateNavButton onClick={handlePreviousMonth}>
                <ArrowLeftIcon />
              </DateNavButton>
              <DateRangeText>{getDateRangeText()}</DateRangeText>
              <DateNavButton
                onClick={handleNextMonth}
                disabled={
                  currentMonth.getFullYear() === new Date().getFullYear()
                  && currentMonth.getMonth() === new Date().getMonth()
                }
              >
                <ArrowRightIcon />
              </DateNavButton>
            </DateRangeContainer>

            {/* バイタル種別タブ */}
            <TabContainer>
              <Tab
                $active={selectedVital === 'temperature'}
                onClick={() => setSelectedVital('temperature')}
              >
                体温
              </Tab>
              <Tab
                $active={selectedVital === 'blood_pressure_pulse'}
                onClick={() => setSelectedVital('blood_pressure_pulse')}
              >
                血圧脈拍
              </Tab>
              {hasSpo2Data && (
              <Tab
                $active={selectedVital === 'spo2'}
                onClick={() => setSelectedVital('spo2')}
              >
                SpO2
              </Tab>
              )}
              {(hasMealData || hasHydrationData) && (
              <Tab
                $active={selectedVital === 'hydration'}
                onClick={() => setSelectedVital('hydration')}
              >
                {hasMealData && hasHydrationData
                  ? '食事水分'
                  : hasMealData
                    ? '食事'
                    : '水分'}
              </Tab>
              )}
            </TabContainer>

            {/* グラフコンテンツ */}
            <Content>
              {/* 月平均値表示 */}
              <MonthlyAverage data={monthlyAverage} />

              {/* グラフ表示 */}
              <GraphCard>
                {renderChart()}
              </GraphCard>
            </Content>
          </>
        )}
      </Container>
    </PageDataBoundary>
  );
};

export default HealthGraphPage;
